// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Auth.tsx
//  Login em 2 passos:
//    1. Credenciais → valida e lista empresas disponíveis
//    2. (se múltiplas empresas) Selecionar empresa → gera JWT
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth, type BloqueioAssinatura, type EmpresaInfo } from '@/hooks/useAuth';
import { apiClient } from '@/lib/apiClient';
import { APP_NOME, APP_TAGLINE } from '@/lib/appMode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { ArrowLeft, Building2, CreditCard, Loader2, LogIn } from 'lucide-react';
import logo from '@/assets/logo.jpg';

// ── Schemas ───────────────────────────────────────────────────────────────

const loginSchema = z.object({
  login: z.string().min(1, 'Informe o login'),
  senha: z.string().min(1, 'Informe a senha'),
});

// ── Componente ────────────────────────────────────────────────────────────

type Step = 'credentials' | 'select-empresa';

export default function Auth() {
  const { user, signIn, getEmpresasDisponiveis } = useAuth();
  const { toast } = useToast();

  const [step,        setStep]        = useState<Step>('credentials');
  const [loading,     setLoading]     = useState(false);
  const [empresas,    setEmpresas]    = useState<EmpresaInfo[]>([]);
  const [credentials, setCredentials] = useState({ login: '', senha: '' });
  const [selectedId,  setSelectedId]  = useState<number | null>(null);
  const [esqueciLoading, setEsqueciLoading] = useState(false);
  const [bloqueio,    setBloqueio]    = useState<BloqueioAssinatura | null>(null);

  if (user) {
    return <Navigate to="/" replace />;
  }

  // ── Passo 1: validar credenciais e descobrir empresas ──────────────────
  const handleCredentials = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const login = (formData.get('login') as string).trim();
    const senha = formData.get('senha') as string;

    try {
      loginSchema.parse({ login, senha });
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({ title: 'Dados inválidos', description: err.errors[0].message, variant: 'destructive' });
        setLoading(false);
        return;
      }
    }

    const { empresas: lista, error, bloqueio: bloq } = await getEmpresasDisponiveis(login, senha);

    if (bloq) {
      // Credenciais válidas, mas assinatura pendente (trial vencido/suspensa/
      // cancelada) — troca o formulário pelo aviso com a ação de pagamento.
      setBloqueio(bloq);
      setLoading(false);
      return;
    }

    if (error) {
      toast({ title: 'Erro ao entrar', description: error.message, variant: 'destructive' });
      setLoading(false);
      return;
    }

    setCredentials({ login, senha });

    if (lista.length === 1) {
      // Apenas uma empresa → login direto
      const { error: loginError } = await signIn(login, senha, lista[0].id);
      if (loginError) {
        toast({ title: 'Erro ao entrar', description: loginError.message, variant: 'destructive' });
      }
    } else {
      // Múltiplas empresas → exibir seletor
      setEmpresas(lista);
      setStep('select-empresa');
    }

    setLoading(false);
  };

  // ── Esqueci minha senha: envia o e-mail de redefinição (sempre 200) ─────
  const handleEsqueciSenha = async () => {
    const login = (document.getElementById('login') as HTMLInputElement | null)?.value.trim() ?? '';
    if (!login) {
      toast({
        title: 'Informe seu e-mail',
        description: 'Preencha o campo de login com seu e-mail e clique novamente.',
      });
      return;
    }
    setEsqueciLoading(true);
    try {
      await apiClient.post('/api/auth/esqueci-senha', { login });
    } catch {
      // Resposta é sempre a mesma (anti-enumeração) — falha de rede cai aqui.
    }
    setEsqueciLoading(false);
    toast({
      title: 'Verifique seu e-mail',
      description: 'Se o e-mail estiver cadastrado, você receberá o link de redefinição em instantes.',
    });
  };

  // ── Passo 2: concluir login com empresa selecionada ────────────────────
  const handleSelectEmpresa = async () => {
    if (!selectedId) return;
    setLoading(true);

    const { error } = await signIn(credentials.login, credentials.senha, selectedId);
    if (error) {
      toast({ title: 'Erro ao entrar', description: error.message, variant: 'destructive' });
    }

    setLoading(false);
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">

        {/* Logo */}
        <div className="flex justify-center">
          <img src={logo} alt="Udisys" className="h-14 w-auto object-contain" />
        </div>

        {bloqueio ? (
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-center">
                {bloqueio.codigo === 'TRIAL_EXPIRADO' ? 'Seu teste terminou' :
                 bloqueio.codigo === 'ASSINATURA_SUSPENSA' ? 'Assinatura suspensa' :
                 'Assinatura cancelada'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">{bloqueio.mensagem}</p>
              {bloqueio.checkoutUrl && (
                <Button asChild className="w-full">
                  <a href={bloqueio.checkoutUrl} target="_blank" rel="noopener noreferrer">
                    <CreditCard className="mr-2 h-4 w-4" />
                    {bloqueio.codigo === 'TRIAL_EXPIRADO' ? 'Assinar agora' : 'Regularizar pagamento'}
                  </a>
                </Button>
              )}
              <p className="text-xs text-muted-foreground text-center">
                Após a confirmação do pagamento, o acesso é liberado automaticamente.
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setBloqueio(null)}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar ao login
              </Button>
            </CardContent>
          </Card>
        ) : step === 'credentials' ? (
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-center">Entrar</CardTitle>
              <CardDescription className="text-center">
                Informe suas credenciais para acessar o sistema
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCredentials} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login">E-mail ou login</Label>
                  <Input
                    id="login"
                    name="login"
                    type="text"
                    placeholder="seu@email.com.br"
                    autoComplete="username"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="senha">Senha</Label>
                  <Input
                    id="senha"
                    name="senha"
                    type="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <LogIn className="mr-2 h-4 w-4" />}
                  {loading ? 'Verificando...' : 'Entrar'}
                </Button>
                <button
                  type="button"
                  onClick={handleEsqueciSenha}
                  disabled={esqueciLoading}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  {esqueciLoading ? 'Enviando...' : 'Esqueci minha senha'}
                </button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-center">Selecione a empresa</CardTitle>
              <CardDescription className="text-center">
                Você tem acesso a {empresas.length} empresas. Escolha com qual deseja entrar.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {empresas.map((emp) => (
                <button
                  key={emp.id}
                  type="button"
                  onClick={() => setSelectedId(emp.id)}
                  className={`w-full text-left rounded-lg border p-3 transition-colors hover:bg-accent ${
                    selectedId === emp.id ? 'border-primary bg-accent' : 'border-border'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {emp.nomeFantasia ?? emp.razaoSocial}
                      </p>
                      {emp.nomeFantasia && (
                        <p className="text-xs text-muted-foreground truncate">{emp.razaoSocial}</p>
                      )}
                      {emp.cnpjCpf && (
                        <p className="text-xs text-muted-foreground">{emp.cnpjCpf}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setStep('credentials'); setSelectedId(null); }}
                  disabled={loading}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  onClick={handleSelectEmpresa}
                  disabled={!selectedId || loading}
                >
                  {loading
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <LogIn className="mr-2 h-4 w-4" />}
                  {loading ? 'Entrando...' : 'Entrar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground">
          {APP_NOME} — {APP_TAGLINE}
        </p>
        <p className="text-center text-xs text-muted-foreground">
          É contador de uma empresa que usa o Udisys? <Link to="/contador/entrar" className="underline">Portal do Contador</Link>
        </p>
      </div>
    </div>
  );
}
